/**
 * FiscalDashboard - Admin Dashboard for FRCS VMS Fiscal Invoices
 * View fiscal invoices, daily reports, and manage refunds
 */
import { useState, useEffect } from 'react';
import { 
  FileText, Calendar, DollarSign, TrendingUp, RefreshCw, 
  Download, Search, Filter, QrCode, Printer, AlertTriangle,
  ChevronLeft, ChevronRight, Eye, RotateCcw
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Select, 
  SelectTrigger, 
  SelectValue, 
  SelectContent, 
  SelectItem 
} from '@/components/ui/select';
import { toast } from 'sonner';
import { api } from '@/utils/apiClient';
import { FiscalReceiptDisplay, printFiscalReceipt } from '@/components/frcs/FiscalReceipt';

// Stats Card Component
const StatCard = ({ title, value, subtitle, icon: Icon, color = 'teal' }) => {
  const colorClasses = {
    teal: 'bg-teal-50 text-teal-600 dark:bg-teal-900/30 dark:text-teal-400',
    green: 'bg-green-50 text-green-600 dark:bg-green-900/30 dark:text-green-400',
    blue: 'bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400',
    red: 'bg-red-50 text-red-600 dark:bg-red-900/30 dark:text-red-400',
    amber: 'bg-amber-50 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400',
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl p-5 border border-gray-200 dark:border-gray-700">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">{title}</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">{value}</p>
          {subtitle && (
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{subtitle}</p>
          )}
        </div>
        <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </div>
  );
};

// Invoice Type Badge
const InvoiceTypeBadge = ({ type }) => {
  const typeConfig = {
    'NS': { label: 'Normal Sale', color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' },
    'NR': { label: 'Normal Refund', color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' },
    'AS': { label: 'Advance Sale', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400' },
    'AR': { label: 'Advance Refund', color: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400' },
    'CS': { label: 'Copy Sale', color: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300' },
    'CR': { label: 'Copy Refund', color: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300' },
    'PS': { label: 'Proforma', color: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400' },
    'PR': { label: 'Proforma Refund', color: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400' },
    'TS': { label: 'Training', color: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400' },
    'TR': { label: 'Training Refund', color: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400' },
  };

  const config = typeConfig[type] || { label: type, color: 'bg-gray-100 text-gray-800' };

  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
      {config.label}
    </span>
  );
};

export default function FiscalDashboard() {
  const [loading, setLoading] = useState(true);
  const [dailyReport, setDailyReport] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [totalInvoices, setTotalInvoices] = useState(0);
  const [vmsStatus, setVmsStatus] = useState(null);
  
  // Filters
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [invoiceTypeFilter, setInvoiceTypeFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Selected invoice for detail view
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  const limit = 20;

  // Fetch VMS status
  const fetchVmsStatus = async () => {
    try {
      const res = await api.get('/frcs-vms/status');
      setVmsStatus(res.data);
    } catch (err) {
      console.error('Failed to fetch VMS status:', err);
    }
  };

  // Fetch daily report
  const fetchDailyReport = async () => {
    try {
      const res = await api.get('/frcs-vms/daily-report', { params: { date: selectedDate } });
      setDailyReport(res.data);
    } catch (err) {
      console.error('Failed to fetch daily report:', err);
    }
  };

  // Fetch invoices
  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const params = {
        limit,
        skip: page * limit
      };
      if (invoiceTypeFilter !== 'all') {
        params.invoice_type = invoiceTypeFilter;
      }

      const res = await api.get('/frcs-vms/fiscal-invoices', { params });
      setInvoices(res.data.invoices || []);
      setTotalInvoices(res.data.total || 0);
    } catch (err) {
      console.error('Failed to fetch invoices:', err);
      toast.error('Failed to load fiscal invoices');
    } finally {
      setLoading(false);
    }
  };

  // Fetch invoice detail
  const fetchInvoiceDetail = async (sdcNumber) => {
    try {
      const res = await api.get(`/frcs-vms/fiscal-invoice/${sdcNumber}`);
      setSelectedInvoice(res.data);
    } catch (err) {
      toast.error('Failed to load invoice details');
    }
  };

  // Copy Invoice
  const handleCopyInvoice = async (sdcNumber) => {
    try {
      const original = selectedInvoice;
      if (!original) return;

      const copyType = original.invoice_type === 'NS' ? 'CS' : 'CR';
      
      const res = await api.post('/frcs-vms/fiscalize', {
        items: original.items.map(item => ({
          name: item.name,
          price: item.price,
          quantity: item.quantity,
          tax_label: item.tax_label || 'G',
        })),
        payment_method: original.payment_method,
        invoice_type: copyType,
        cashier_name: 'Admin',
        reference_number: sdcNumber,
      });

      if (res.data.success) {
        toast.success('Copy invoice created');
        printFiscalReceipt(res.data.fiscal_invoice);
        fetchInvoices();
      }
    } catch (err) {
      toast.error('Failed to create copy invoice');
    }
  };

  // Refund Invoice
  const handleRefundInvoice = async (sdcNumber) => {
    if (!confirm('Are you sure you want to issue a refund for this invoice?')) {
      return;
    }

    try {
      const res = await api.post(`/frcs-vms/refund/${sdcNumber}`, null, {
        params: { cashier_name: 'Admin', reason: 'Admin refund' }
      });

      if (res.data.success) {
        toast.success('Refund invoice created');
        printFiscalReceipt(res.data.fiscal_invoice);
        fetchInvoices();
        setSelectedInvoice(null);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create refund invoice');
    }
  };

  // Initial load
  useEffect(() => {
    fetchVmsStatus();
    fetchDailyReport();
    fetchInvoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reload on filter change
  useEffect(() => {
    fetchInvoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, invoiceTypeFilter]);

  // Reload daily report on date change
  useEffect(() => {
    fetchDailyReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  const totalPages = Math.ceil(totalInvoices / limit);

  // If showing invoice detail
  if (selectedInvoice) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <div className="mb-6">
          <Button 
            variant="ghost" 
            onClick={() => setSelectedInvoice(null)}
            className="text-gray-600 dark:text-gray-300"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back to List
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Invoice Details */}
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
              Invoice Details
            </h2>
            <dl className="space-y-3">
              <div className="flex justify-between">
                <dt className="text-gray-500">SDC Number:</dt>
                <dd className="font-mono font-medium text-gray-900 dark:text-white">
                  {selectedInvoice.sdc_number}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Type:</dt>
                <dd><InvoiceTypeBadge type={selectedInvoice.invoice_type} /></dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Date/Time:</dt>
                <dd className="text-gray-900 dark:text-white">{selectedInvoice.sdc_time}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Cashier:</dt>
                <dd className="text-gray-900 dark:text-white">{selectedInvoice.cashier}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Total:</dt>
                <dd className="font-bold text-gray-900 dark:text-white">
                  ${selectedInvoice.total?.toFixed(2)}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Tax (12.5% VAT):</dt>
                <dd className="text-gray-900 dark:text-white">
                  ${selectedInvoice.total_tax?.toFixed(2)}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Payment Method:</dt>
                <dd className="text-gray-900 dark:text-white">{selectedInvoice.payment_method}</dd>
              </div>
            </dl>

            <div className="mt-6 flex gap-3">
              <Button 
                onClick={() => printFiscalReceipt(selectedInvoice)}
                className="flex-1 bg-teal-600 hover:bg-teal-700"
              >
                <Printer className="w-4 h-4 mr-2" />
                Print Receipt
              </Button>
            </div>
            
            {/* Copy & Refund Actions */}
            {['NS', 'AS'].includes(selectedInvoice.invoice_type) && (
              <div className="mt-3 flex gap-3">
                <Button 
                  variant="outline"
                  onClick={() => handleCopyInvoice(selectedInvoice.sdc_number)}
                  className="flex-1"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Copy Invoice
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => handleRefundInvoice(selectedInvoice.sdc_number)}
                  className="flex-1 text-red-600 hover:bg-red-50"
                >
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Issue Refund
                </Button>
              </div>
            )}
          </div>

          {/* Receipt Preview */}
          <div className="bg-gray-100 dark:bg-gray-900 rounded-xl p-4">
            <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3 text-center">
              Receipt Preview
            </h3>
            <FiscalReceiptDisplay 
              fiscalInvoice={selectedInvoice}
              showPrintButton={false}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            FRCS Fiscal Dashboard
          </h1>
          <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">
            Fiji VAT Monitoring System Compliance
          </p>
        </div>
        <div className="flex items-center gap-3">
          {vmsStatus && (
            <div className={`px-3 py-1.5 rounded-full text-sm font-medium flex items-center gap-2 ${
              vmsStatus.status === 'connected' 
                ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
            }`}>
              <div className={`w-2 h-2 rounded-full ${
                vmsStatus.status === 'connected' ? 'bg-green-500' : 'bg-red-500'
              }`} />
              {vmsStatus.environment === 'sandbox' ? 'Sandbox' : 'Production'}
            </div>
          )}
          <Button 
            variant="outline" 
            onClick={() => { fetchDailyReport(); fetchInvoices(); }}
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* VMS Info */}
      {vmsStatus && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <QrCode className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5" />
            <div>
              <p className="font-medium text-blue-800 dark:text-blue-300">
                VMS Connected: {vmsStatus.taxpayer_name}
              </p>
              <p className="text-sm text-blue-600 dark:text-blue-400">
                TIN: {vmsStatus.taxpayer_tin} | UID: {vmsStatus.developer_uid} | 
                Invoices Today: {vmsStatus.invoice_counter}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Date Selector */}
      <div className="flex items-center gap-4">
        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
          Report Date:
        </label>
        <Input
          type="date"
          value={selectedDate}
          onChange={(e) => setSelectedDate(e.target.value)}
          className="w-48"
        />
      </div>

      {/* Daily Stats */}
      {dailyReport && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <StatCard
            title="Total Invoices"
            value={dailyReport.total_invoices}
            subtitle={`Date: ${dailyReport.date}`}
            icon={FileText}
            color="blue"
          />
          <StatCard
            title="Total Sales"
            value={`$${dailyReport.total_sales.toFixed(2)}`}
            subtitle="Gross sales"
            icon={TrendingUp}
            color="green"
          />
          <StatCard
            title="Total Refunds"
            value={`$${dailyReport.total_refunds.toFixed(2)}`}
            subtitle="Returned"
            icon={AlertTriangle}
            color="red"
          />
          <StatCard
            title="Net Sales"
            value={`$${dailyReport.net_sales.toFixed(2)}`}
            subtitle="After refunds"
            icon={DollarSign}
            color="teal"
          />
          <StatCard
            title="VAT Collected"
            value={`$${dailyReport.total_tax_collected.toFixed(2)}`}
            subtitle="12.5% VAT"
            icon={FileText}
            color="amber"
          />
        </div>
      )}

      {/* Payment Breakdown */}
      {dailyReport?.payment_breakdown?.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl p-5 border border-gray-200 dark:border-gray-700">
          <h3 className="font-semibold text-gray-900 dark:text-white mb-4">
            Payment Methods
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {dailyReport.payment_breakdown.map((payment) => (
              <div key={payment.method} className="text-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  ${payment.amount.toFixed(2)}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {payment.method_name} ({payment.count})
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Invoices List */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-gray-900 dark:text-white">
              Fiscal Invoices ({totalInvoices})
            </h3>
            <div className="flex items-center gap-3">
              <Select value={invoiceTypeFilter} onValueChange={setInvoiceTypeFilter}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="NS">Normal Sale</SelectItem>
                  <SelectItem value="NR">Normal Refund</SelectItem>
                  <SelectItem value="AS">Advance Sale</SelectItem>
                  <SelectItem value="AR">Advance Refund</SelectItem>
                  <SelectItem value="PS">Proforma</SelectItem>
                  <SelectItem value="TS">Training</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="p-8 text-center text-gray-500">Loading...</div>
        ) : invoices.length === 0 ? (
          <div className="p-8 text-center text-gray-500">No fiscal invoices found</div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      SDC Number
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      Type
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      Date/Time
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      Cashier
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      Total
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      Tax
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {invoices.map((invoice) => (
                    <tr key={invoice.sdc_number} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                      <td className="px-4 py-3 font-mono text-sm text-gray-900 dark:text-white">
                        {invoice.sdc_number}
                      </td>
                      <td className="px-4 py-3">
                        <InvoiceTypeBadge type={invoice.invoice_type} />
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                        {invoice.sdc_time}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                        {invoice.cashier}
                      </td>
                      <td className="px-4 py-3 text-right text-sm font-medium text-gray-900 dark:text-white">
                        ${invoice.total?.toFixed(2)}
                      </td>
                      <td className="px-4 py-3 text-right text-sm text-gray-600 dark:text-gray-300">
                        ${invoice.total_tax?.toFixed(2)}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => fetchInvoiceDetail(invoice.sdc_number)}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
                <p className="text-sm text-gray-500">
                  Page {page + 1} of {totalPages}
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(Math.max(0, page - 1))}
                    disabled={page === 0}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
                    disabled={page >= totalPages - 1}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
